import { Heading1, Paragraph } from "@sb1/ffe-core-react";
import classNames from "classnames";
import type { FC, ReactNode } from "react";
import Progressbar from "./Progressbar";
import useFlytHeader from "./useFlytHeader";
import GridRow from "../ffe-grid/components/GridRow";
import type { SizeModifier } from "../ffe-grid/ffeGridUtils";
import loFavorLogo from "$IMAGES/lo_favor.svg";
import loFavorLogoDM from "$IMAGES/lo_favor_darkmode.svg";
import { GridCol } from "~/shared/components/ffe-grid";
import useDarkMode from "~/shared/hooks/use-darkmode-hook/use-darkmode-hook";
import mbc from "~/shared/functions/mbc";
import { BackButton, GaTilMinPensjon } from "../ffe-buttons";
import { Link } from "../link";

type DynamicArraySize<N extends number | undefined, T extends number[] = []> = N extends undefined ? number[] : N extends T["length"] ? T : DynamicArraySize<N, [...T, number]>;

export interface StegProps<T extends "single" | "array" = "single", N extends number | undefined = undefined> {
	steg: T extends "single" ? number : DynamicArraySize<N>;
}

export interface FlytHeaderProps {
	header?: string;
	headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
	leadText?: string;
	imgBottom?: ReactNode;
	imgTop?: ReactNode;
	imgRight?: string;
	progress?: number;
	darkBG?: boolean;
	bottomPadding?: boolean;
	backButton?: boolean;
	className?: string;
	sm?: SizeModifier;
	md?: SizeModifier;
	lg?: SizeModifier;
}

const FlytHeader: FC<FlytHeaderProps> = ({ header, headingLevel, leadText, imgBottom, imgTop, imgRight, progress, darkBG, bottomPadding, backButton, className, ...grid }) => {
	const { antallSider, loFavor } = useFlytHeader();
	const background = darkBG ? "ppm-row-bg-vann" : "ppm-background__subtle";
	const [darkMode] = useDarkMode();

	return (
		<>
			{backButton && !mbc.isNative() && (
				<GridRow oldColorClass={classNames(background, "ppm-dm-bg--natt")}>
					<GridCol>
						<BackButton className="ppm-portal-back-button" as={Link} to="/">
							<GaTilMinPensjon />
						</BackButton>
					</GridCol>
				</GridRow>
			)}
			{loFavor && (
				<GridRow oldColorClass={classNames(background, "ppm-dm-bg--natt")} className="ppm-grid__row--padding-top">
					<GridCol center={true}>
						<img alt="LOfavør" src={darkBG || darkMode ? loFavorLogoDM : loFavorLogo} width="100" />
					</GridCol>
				</GridRow>
			)}
			<GridRow
				oldColorClass={classNames(background, "ppm-dm-bg--natt")}
				className={classNames({ "ppm-grid__row--padding-top": !(progress && antallSider) }, { "ppm-flyt-header": !imgRight }, className)}
			>
				{progress !== undefined && antallSider !== undefined && (
					<GridCol>
						<Progressbar value={progress} max={antallSider} darkBG={darkBG} />
					</GridCol>
				)}
				{imgTop}
				<GridCol {...grid} centerText={true} className={classNames({ "ppm-grid__col--padding-bottom": bottomPadding }, { "ppm-flyt-header--side-img": imgRight })}>
					<div>
						{header && (
							<Heading1
								noMargin={!imgBottom && !leadText}
								lookLike={headingLevel || 1}
								className={classNames("ppm-dm-tekst--vann-70", { "ppm-portal-white-text": darkBG })}
							>
								{header}
							</Heading1>
						)}
						{leadText && (
							<Paragraph lead={true} className={classNames({ "ppm-portal-white-text": darkBG })}>
								{leadText}
							</Paragraph>
						)}
					</div>
					{/* Muligens noe vi burde se mer på i fremtiden */}
					{imgRight && <img alt="" src={imgRight} className="ppm-flyt-header__img-right" />}
				</GridCol>
				{imgBottom}
			</GridRow>
		</>
	);
};
export default FlytHeader;
